import type {
  LdClientMessage,
  LdRoomListEntry,
  LdRoomSnapshot,
  LdServerMessage,
  LdSessionStateSnapshot,
} from "../../../lib/multiplayer/loot-drop-protocol";
import { ldConnectionTimeoutMs, ldWsUrl } from "../../../lib/multiplayer/loot-drop-protocol";

type Listener = (msg: LdServerMessage) => void;

export class LdClient {
  private ws: WebSocket | null = null;
  private listeners = new Set<Listener>();
  private connectPromise: Promise<void> | null = null;
  playerId: string | null = null;
  room: LdRoomSnapshot | null = null;
  roomsList: LdRoomListEntry[] = [];
  connected = false;
  lastSessionState: LdSessionStateSnapshot | null = null;
  lastConnectUrl: string | null = null;

  connect(url?: string): Promise<void> {
    const target = url ?? ldWsUrl();
    this.lastConnectUrl = target;
    if (this.connectPromise) return this.connectPromise;

    this.connectPromise = new Promise((resolve, reject) => {
      if (this.ws) this.disconnect();
      const ws = new WebSocket(target);
      this.ws = ws;
      let settled = false;
      const timeoutMs = ldConnectionTimeoutMs(target);

      const fail = (err: Error) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        this.connectPromise = null;
        try {
          ws.close();
        } catch {
          /* ignore */
        }
        reject(err);
      };

      const timer = setTimeout(() => fail(new Error("connection_timeout")), timeoutMs);

      ws.onopen = () => {
        this.connected = true;
      };
      ws.onerror = () => fail(new Error("WebSocket connection failed"));
      ws.onclose = () => {
        this.connected = false;
        this.ws = null;
        this.connectPromise = null;
        if (!settled) fail(new Error("WebSocket closed before ready"));
      };
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(String(ev.data)) as LdServerMessage;
          this.handleServerMessage(msg);
          for (const fn of this.listeners) fn(msg);
          if (!settled && msg.type === "connected") {
            settled = true;
            clearTimeout(timer);
            this.connectPromise = null;
            resolve();
          }
        } catch {
          /* ignore */
        }
      };
    });

    return this.connectPromise;
  }

  disconnect(): void {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.connected = false;
    this.connectPromise = null;
    this.playerId = null;
    this.room = null;
    this.roomsList = [];
    this.lastSessionState = null;
  }

  onMessage(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  send(msg: LdClientMessage): boolean {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return false;
    this.ws.send(JSON.stringify(msg));
    return true;
  }

  listRooms(accountId?: string | null, friendAccountIds?: string[]): boolean {
    return this.send({
      type: "list_rooms",
      ...(accountId ? { accountId } : {}),
      ...(friendAccountIds?.length ? { friendAccountIds } : {}),
    });
  }

  createRoom(
    name: string,
    accountId: string,
    maxCursors: number,
    onlyFriends: boolean,
    sessionDotsLimit: number,
    equippedCosmeticIds?: string[],
    dotsBalance?: number,
    cursorColor?: string,
  ): boolean {
    return this.send({
      type: "create_room",
      name,
      accountId,
      maxCursors,
      onlyFriends,
      sessionDotsLimit,
      ...(typeof dotsBalance === "number" ? { dotsBalance } : {}),
      ...(equippedCosmeticIds?.length ? { equippedCosmeticIds } : {}),
      ...(cursorColor ? { cursorColor } : {}),
    });
  }

  joinRoom(
    code: string,
    name: string,
    accountId: string,
    equippedCosmeticIds?: string[],
    dotsBalance?: number,
    cursorColor?: string,
  ): boolean {
    return this.send({
      type: "join_room",
      code: code.trim().toUpperCase(),
      name,
      accountId,
      ...(typeof dotsBalance === "number" ? { dotsBalance } : {}),
      ...(equippedCosmeticIds?.length ? { equippedCosmeticIds } : {}),
      ...(cursorColor ? { cursorColor } : {}),
    });
  }

  leaveRoom(): boolean {
    return this.send({ type: "leave_room" });
  }

  endRoom(): boolean {
    return this.send({ type: "end_room" });
  }

  spawnDrop(
    amount: number,
    x: number,
    y: number,
    cosmetic?: { cosmeticId: string; cosmeticName: string; cosmeticImageData?: string },
  ): boolean {
    return this.send({
      type: "spawn_drop",
      amount,
      x,
      y,
      ...(cosmetic
        ? {
            cosmeticId: cosmetic.cosmeticId,
            cosmeticName: cosmetic.cosmeticName,
            ...(cosmetic.cosmeticImageData ? { cosmeticImageData: cosmetic.cosmeticImageData } : {}),
          }
        : {}),
    });
  }

  setFreezeDots(enabled: boolean): boolean {
    return this.send({ type: "set_freeze_dots", enabled });
  }

  rainLaunch(ballCount: number, amountPerBall: number): boolean {
    return this.send({ type: "rain_launch", ballCount, amountPerBall });
  }

  kickPlayer(playerId: string): boolean {
    return this.send({ type: "kick_player", playerId });
  }

  blockPlayer(playerId: string): boolean {
    return this.send({ type: "block_player", playerId });
  }

  sendPos(x: number, y: number, seq: number): boolean {
    return this.send({ type: "pos", x, y, seq });
  }

  isHost(): boolean {
    return !!(this.playerId && this.room && this.room.hostId === this.playerId);
  }

  private handleServerMessage(msg: LdServerMessage): void {
    if (msg.type === "connected") {
      this.playerId = msg.playerId;
    }
    if (msg.type === "rooms_list") {
      this.roomsList = msg.rooms;
    }
    if (msg.type === "room_state") {
      this.room = msg.room;
    }
    if (msg.type === "session_state") {
      this.lastSessionState = msg.state;
      if (this.room) {
        this.room.hostDotsBalance = msg.state.hostDotsBalance;
        this.room.freezeDots = msg.state.freezeDots;
        this.room.sessionDotsLimit = msg.state.sessionDotsLimit;
        this.room.sessionDotsSpent = msg.state.sessionDotsSpent;
        this.room.drops = msg.state.drops;
        this.room.pickupFx = msg.state.pickupFx;
        for (const sp of msg.state.players) {
          const p = this.room.players.find((x) => x.id === sp.id);
          if (p) {
            p.x = sp.x;
            p.y = sp.y;
            if (typeof sp.dotsEarned === "number") p.dotsEarned = sp.dotsEarned;
          }
        }
      }
    }
    if (msg.type === "session_ended" || msg.type === "kicked") {
      this.room = null;
      this.lastSessionState = null;
    }
  }
}

export const ldClient = new LdClient();
